import React, { useState, useEffect } from 'react';
import { isDocumentExcludedFromLibrary } from './sharepointHelpers';

import { 
  Folder, 
  FileText, 
  ChevronRight, 
  ChevronDown, 
  Check,
  Minus,
  FileImage,
  FileSpreadsheet,
  Presentation,
  FileArchive,
  FileCode,
  Music,
  Video,
  File
} from 'lucide-react';
import { SharePointListItem } from '../../types/sharepoint.types';
import { SharePointData } from '../../types/SharePointData';

interface TreeViewProps {
  items: SharePointListItem[];
  checkedItems: { [key: string]: boolean };
  onCheckboxChange: (itemId: string, checked: boolean, sharepointId: number) => void;
  sharepointId: number;
  onDocumentExclusion?: (sharepointId: number, documentId: string, libraryId: string, exclude: boolean) => void;
  isDocumentExcluded?: (documentId: string, libraryId: string, parentIds: string[], sharepoint: any) => boolean;
  libraryId?: string;
  sharepoint: SharePointData;
}

interface CheckboxState {
  checked: boolean;
  indeterminate: boolean;
}

const TreeView: React.FC<TreeViewProps> = ({
  items,
  checkedItems,
  onCheckboxChange,
  sharepointId,
  onDocumentExclusion,
  isDocumentExcluded,
  libraryId = '',
  sharepoint 
}) => {
  const [expandedFolders, setExpandedFolders] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const expandAllFolders = (items: SharePointListItem[]) => {
      const expanded: { [key: string]: boolean } = {};
      
      const expandRecursively = (itemList: SharePointListItem[]) => {
        itemList.forEach(item => {
          if (item.is_folder) {
            expanded[item.id] = true;
            if (item.children) {
              expandRecursively(item.children as SharePointListItem[]);
            }
          }
        });
      };
      
      expandRecursively(items);
      return expanded;
    };

    setExpandedFolders(expandAllFolders(items));
  }, [items]);

  const getParentIds = (itemId: string, items: SharePointListItem[], currentPath: string[] = []): string[] => {
    for (const item of items) {
      if (item.id === itemId) {
        return currentPath;
      }
      if (item.is_folder && item.children) {
        const found = getParentIds(itemId, item.children as SharePointListItem[], [...currentPath, item.id]);
        if (found.length > 0 || found === currentPath) {
          return found;
        }
      }
    }
    return [];
  };

  const isItemExcluded = (item: SharePointListItem): boolean => {
    if (!sharepoint) return false;
    
    const parentIds = getParentIds(item.id, items);
    
    return isDocumentExcludedFromLibrary(
      item.id,
      libraryId || '',
      parentIds,
      sharepoint.excludedItems
    );
  };

  const calculateCheckboxState = (item: SharePointListItem): CheckboxState => {
    if (!item.is_folder || !item.children || (item.children as SharePointListItem[]).length === 0) {
      return {
        checked: !isItemExcluded(item),
        indeterminate: false
      };
    }

    const children = item.children as SharePointListItem[];
    let includedChildren = 0;
    let excludedChildren = 0;

    children.forEach(child => {
      const childState = calculateCheckboxState(child);
      if (childState.indeterminate) {
        includedChildren++;
        excludedChildren++;
      } else if (childState.checked) {
        includedChildren++;
      } else {
        excludedChildren++;
      }
    });

    const totalChildren = children.length;
    
    if (includedChildren === totalChildren && excludedChildren === 0) {
      return { checked: true, indeterminate: false };
    } else if (excludedChildren === totalChildren && includedChildren === 0) {
      return { checked: false, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const handleFolderClick = (folderId: string) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const updateSingleItem = (item: SharePointListItem, exclude: boolean) => {
    if (!onDocumentExclusion || !libraryId) return;
    onDocumentExclusion(sharepointId, item.id, libraryId, exclude);
  };

  const updateItemAndAllChildren = (item: SharePointListItem, exclude: boolean) => {
    if (!onDocumentExclusion || !libraryId) return;
    
    onDocumentExclusion(sharepointId, item.id, libraryId, exclude);
    
    if (item.is_folder && item.children) {
      const updateChildren = (children: SharePointListItem[]) => {
        children.forEach((child: SharePointListItem) => {
          onDocumentExclusion(sharepointId, child.id, libraryId, exclude);
          if (child.is_folder && child.children) {
            updateChildren(child.children as SharePointListItem[]);
          }
        });
      };
      
      updateChildren(item.children as SharePointListItem[]);
    }
  };

  const handleItemCheckboxChange = (item: SharePointListItem, targetState: boolean) => {
    if (item.is_folder) {
      updateItemAndAllChildren(item, !targetState);
    } else {
      updateSingleItem(item, !targetState);
    }
  };

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return <File className="h-4 w-4 text-gray-600" />;
    
    const type = fileType.toLowerCase();
    
    if (type.includes('pdf')) {
      return <FileText className="h-4 w-4 text-red-600" />;
    }
    
    if (type.includes('word') || type.includes('document')) {
      return <FileText className="h-4 w-4 text-blue-600" />;
    }
    
    if (type.includes('excel') || type.includes('spreadsheet')) {
      return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
    }
    
    if (type.includes('powerpoint') || type.includes('presentation')) {
      return <Presentation className="h-4 w-4 text-orange-600" />;
    }
    
    if (type.includes('image') || type.includes('jpeg') || type.includes('png') || type.includes('gif')) {
      return <FileImage className="h-4 w-4 text-purple-600" />;
    }
    
    if (type.includes('video') || type.includes('mp4') || type.includes('avi') || type.includes('mov')) {
      return <Video className="h-4 w-4 text-pink-600" />;
    }
    
    if (type.includes('audio') || type.includes('mp3') || type.includes('wav')) {
      return <Music className="h-4 w-4 text-indigo-600" />;
    }
    
    if (type.includes('zip') || type.includes('rar') || type.includes('7z')) {
      return <FileArchive className="h-4 w-4 text-yellow-600" />;
    }
    
    if (type.includes('json') || type.includes('xml') || type.includes('code')) {
      return <FileCode className="h-4 w-4 text-cyan-600" />;
    }
    
    return <File className="h-4 w-4 text-gray-600" />;
  };

  const renderCheckbox = (state: CheckboxState, onClick: () => void) => {
    return (
      <div
        className={`h-5 w-5 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
          state.checked 
            ? 'bg-black border-black' 
            : state.indeterminate 
              ? 'bg-gray-400 border-gray-400' 
              : 'bg-white'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {state.checked && <Check className="h-3 w-3 text-white" />}
        {state.indeterminate && <Minus className="h-3 w-3 text-white" />}
      </div>
    );
  };

  const renderTreeItems = (items: SharePointListItem[], depth = 0) => {
    return items.map((item: SharePointListItem) => {
      const itemState = calculateCheckboxState(item);
      
      return (
        <div key={item.id} className="w-full">
          <div 
            style={{ paddingLeft: depth * 20 }} 
            className="flex items-center p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
          >
            {item.is_folder && (
              <div 
                className="mr-2 p-1 hover:bg-gray-200 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFolderClick(item.id);
                }}
              >
                {expandedFolders[item.id] ? 
                  <ChevronDown className="h-4 w-4 text-gray-600" /> : 
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                }
              </div>
            )}
            
            {!item.is_folder && (
              <div className="w-6 mr-2"></div>
            )}

            <div className="mr-3">
              {renderCheckbox(itemState, () => {
                let targetState: boolean;
                
                if (itemState.indeterminate) {
                  targetState = true;
                } else {
                  targetState = !itemState.checked;
                }
                
                handleItemCheckboxChange(item, targetState);
              })}
            </div>

            <div className="mr-3">
              {item.is_folder ? (
                <Folder className="h-4 w-4 text-yellow-600" />
              ) : (
                getFileIcon(item.file_type)
              )}
            </div>

            <span 
              className="text-sm select-none flex-1 truncate"
              onClick={() => item.is_folder && handleFolderClick(item.id)}
            >
              {item.name}
            </span>
          </div>

          {item.is_folder && 
           expandedFolders[item.id] && 
           item.children && 
           (item.children as SharePointListItem[]).length > 0 && (
            <div>
              {renderTreeItems(item.children as SharePointListItem[], depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-semibold mb-2">No documents found</h3>
        <p className="text-gray-600">No documents available or you may not have permission to view them.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="max-h-96 overflow-y-auto">
        {renderTreeItems(items)}
      </div>
    </div>
  );
};

export default TreeView;